import { useCallback, useEffect, useRef, useState } from 'react';

import type { CapturedFrame } from '../capture/captureFrame';

export type ReplaceCapture = (next: CapturedFrame | null) => void;

/**
 * The frame open for markup. A captured bitmap holds its decoded pixels until
 * it is closed, so replacing the frame — with a new capture, or with null once
 * it is attached, discarded or dropped for a new cut — closes the old one, and
 * so does unmounting. Tracked in a ref rather than an effect cleanup: Strict
 * Mode re-runs effects on mount, which would close a frame still on screen.
 */
export function useCapturedFrame(): [CapturedFrame | null, ReplaceCapture] {
  const [capture, setCapture] = useState<CapturedFrame | null>(null);
  const currentRef = useRef<CapturedFrame | null>(null);

  const replaceCapture = useCallback<ReplaceCapture>((next) => {
    const previous = currentRef.current;
    currentRef.current = next;
    setCapture(next);
    if (previous && previous !== next) previous.bitmap.close();
  }, []);

  useEffect(() => {
    const current = currentRef;
    return () => current.current?.bitmap.close();
  }, []);

  return [capture, replaceCapture];
}
